import React, { useMemo } from 'react'
import { useDispatch } from 'react-redux'
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Row,
  Col,
  Alert,
  Badge,
  Spinner,
} from 'reactstrap'
import { Formik, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import { 
  FaInfoCircle, 
  FaArrowRight, 
  FaHistory, 
  FaCheck,
  FaFileAlt
} from 'react-icons/fa'
import { reviseProposal } from '../../store/slices/transportRequestSlice'

const ReviseProposal = ({ isOpen, toggle, requestId, proposal }) => {
  const dispatch = useDispatch()

  const calculateTotals = (transportRate, additionalCharges, advancePercentage) => {
    const rate = parseFloat(transportRate) || 0
    const additional = parseFloat(additionalCharges) || 0
    const totalFreight = rate + additional
    const percentage = parseFloat(advancePercentage) || 0
    const advanceAmount = (totalFreight * percentage) / 100
    return { totalFreight, advanceAmount }
  }

  if (!proposal || !proposal.counter_proposal) {
    return null
  }

  const validationSchema = Yup.object().shape({
    transport_rate: Yup.number()
      .required('Transport rate is required')
      .positive('Transport rate must be positive')
      .min(1, 'Transport rate must be at least 1'),
    advance_percentage: Yup.number()
      .required('Advance percentage is required')
      .min(0, 'Advance percentage cannot be negative')
      .max(100, 'Advance percentage cannot exceed 100'),
    payment_terms: Yup.string()
      .required('Payment terms are required')
      .min(10, 'Payment terms must be at least 10 characters'),
    additional_charges: Yup.number()
      .min(0, 'Additional charges cannot be negative')
      .default(0),
    special_terms: Yup.string(),
    revision_notes: Yup.string()
      .required('Revision notes are required')
      .min(20, 'Revision notes must be at least 20 characters'),
  })

  const counter = proposal.counter_proposal

  const initialValues = {
    transport_rate: counter.transport_rate || '',
    advance_percentage: counter.advance_percentage || '',
    payment_terms: counter.payment_terms || '',
    additional_charges: counter.additional_charges || 0,
    special_terms: counter.special_terms || '',
    revision_notes: '',
  }

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    const revisionData = {
      ...values,
      transport_rate: parseFloat(values.transport_rate),
      advance_percentage: parseFloat(values.advance_percentage),
      additional_charges: parseFloat(values.additional_charges) || 0,
    }

    const result = await dispatch(
      reviseProposal({ requestId, proposalId: proposal._id, revisionData })
    ).unwrap()

    if (result) {
      resetForm()
      toggle()
    }
    setSubmitting(false)
  }

  const getVersionNumber = proposalNumber => {
    const versionMatch = proposalNumber.match(/-V(\d+)$/)
    return versionMatch ? parseInt(versionMatch[1]) : 1
  }

  const currentVersion = getVersionNumber(proposal.proposal_number)
  const nextVersion = currentVersion + 1

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" className="aurora-modal">
      <ModalHeader toggle={toggle} className="border-0">
        <span className="neo-text">Refine Logistics Protocol</span>
        <Badge color="primary" className="ms-2 status-pill">
          MANIFEST V{nextVersion}
        </Badge>
      </ModalHeader>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, handleSubmit, isSubmitting }) => {
          const { totalFreight, advanceAmount } = calculateTotals(
            values.transport_rate,
            values.additional_charges,
            values.advance_percentage
          )

          return (
            <Form onSubmit={handleSubmit}>
              <ModalBody className="p-4">
                <div className="alert-glass-info mb-4 p-3 rounded-3 d-flex align-items-center">
                  <FaInfoCircle className="me-3 text-info" size={24} />
                  <div>
                    <h6 className="mb-0 fw-bold">Version Control Active</h6>
                    <small>Generating version V{nextVersion} for manifest {proposal.proposal_number}. Values pre-initialized from counter-protocol.</small>
                  </div>
                </div>

                <div className="version-flow mb-4 p-3 glass-effect rounded-3 border-glass">
                  <small className="text-muted d-block mb-2">Protocol Evolution:</small>
                  <div className="d-flex align-items-center gap-2">
                    <Badge color="secondary" className="status-pill">V1</Badge>
                    <FaArrowRight size={10} className="text-muted" />
                    <Badge color="warning" className="status-pill">V{currentVersion}</Badge>
                    <FaArrowRight size={10} className="text-muted" />
                    <Badge color="primary" className="status-pill">V{nextVersion}</Badge>
                  </div>
                </div>

                <Row className="g-3">
                  <Col md={6}>
                    <FormGroup>
                      <Label className="detail-label">Rate Protocol (₹/Unit)</Label>
                      <Field
                        name="transport_rate"
                        type="number"
                        className="glass-input form-control"
                        placeholder="e.g. 2400"
                      />
                      <ErrorMessage name="transport_rate" component="div" className="text-danger small mt-1" />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label className="detail-label">Advance Ratio (%)</Label>
                      <Field
                        name="advance_percentage"
                        type="number"
                        className="glass-input form-control"
                        placeholder="e.g. 30"
                      />
                      <ErrorMessage name="advance_percentage" component="div" className="text-danger small mt-1" />
                    </FormGroup>
                  </Col>
                  <Col md={12}>
                    <FormGroup>
                      <Label className="detail-label">Deviational Charges (₹)</Label>
                      <Field
                        name="additional_charges"
                        type="number"
                        className="glass-input form-control"
                        placeholder="e.g. 500"
                      />
                      <ErrorMessage name="additional_charges" component="div" className="text-danger small mt-1" />
                    </FormGroup>
                  </Col>
                  <Col md={12}>
                    <FormGroup>
                      <Label className="detail-label">Payment Protocols</Label>
                      <Field
                        name="payment_terms"
                        as="textarea"
                        rows="2"
                        className="glass-input form-control"
                      />
                      <ErrorMessage name="payment_terms" component="div" className="text-danger small mt-1" />
                    </FormGroup>
                  </Col>
                  <Col md={12}>
                    <FormGroup>
                      <Label className="detail-label">Special Covenants</Label>
                      <Field
                        name="special_terms"
                        as="textarea"
                        rows="2"
                        className="glass-input form-control"
                      />
                    </FormGroup>
                  </Col>
                  <Col md={12}>
                    <FormGroup>
                      <Label className="detail-label">Strategic Revision Notes</Label>
                      <Field
                        name="revision_notes"
                        as="textarea"
                        rows="3"
                        className="glass-input form-control"
                        placeholder="Detail the logic behind this revision..."
                      />
                      <ErrorMessage name="revision_notes" component="div" className="text-danger small mt-1" />
                    </FormGroup>
                  </Col>
                </Row>

                <div className="proposal-summary mt-4 p-3 glass-effect rounded-3 border-glass">
                  <h6 className="neo-text mb-3">Revised Protocol Summary</h6>
                  <Row className="g-3">
                    <Col md={6}>
                      <div className="mini-detail">
                        <span className="label">Rate Protocol</span>
                        <span className="value">₹{parseFloat(values.transport_rate || 0).toFixed(2)}</span>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mini-detail">
                        <span className="label">Deviation Load</span>
                        <span className="value">₹{parseFloat(values.additional_charges || 0).toFixed(2)}</span>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mini-detail">
                        <span className="label">Total Freight</span>
                        <span className="value text-primary fw-bold">₹{totalFreight.toFixed(2)}</span>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mini-detail">
                        <span className="label">Advance Protocol ({values.advance_percentage}%)</span>
                        <span className="value text-success fw-bold">₹{advanceAmount.toFixed(2)}</span>
                      </div>
                    </Col>
                  </Row>
                </div>
              </ModalBody>

              <ModalFooter className="border-0">
                <Button color="secondary" className="glass-effect" onClick={toggle} disabled={isSubmitting}>
                  Abort
                </Button>
                <Button color="primary" className="btn-gradient" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? <Spinner size="sm" className="me-2" /> : <FaCheck className="me-2" />}
                  Synchronize Manifest V{nextVersion}
                </Button>
              </ModalFooter>
            </Form>
          )
        }}
      </Formik>
    </Modal>
  )
}

export default ReviseProposal
